import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useState } from 'react';
import { PlaylistsListItem } from 'types/playlists.types';
import { PlaylistsSelectList } from 'app/components/Playlist/PlaylistsSelectList';
import { PLAYLISTS_SELECT_SCROLL_ID } from 'app/components/Playlist/PlaylistsSelectList/constants';
import { useIsMounted } from 'app/utils/hooks';

interface CreatePlaylistDialogComponentProps {
  open: boolean;
  playlists: PlaylistsListItem[];
  playlistsLoading: boolean;
  limitReached: boolean;
  maxPlaylistAudiosCount: number;
  addAudioToPlaylists: (
    playlists: PlaylistsListItem[]
  ) => Promise<{ failedCount: number; addedPlaylists: string[] }>;
  createPlaylist: () => void;
  onClose: () => void;
  showLimitReachedPopup: () => void;
}

export const CreatePlaylistDialogComponent = ({
  open: openProp,
  playlists,
  playlistsLoading,
  limitReached,
  maxPlaylistAudiosCount,
  addAudioToPlaylists,
  createPlaylist,
  onClose,
  showLimitReachedPopup,
}: CreatePlaylistDialogComponentProps) => {
  const [open, setOpen] = useState(openProp);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<PlaylistsListItem[]>([]);
  const isMounted = useIsMounted();

  const handleSelectPlaylist = (playlist: PlaylistsListItem) => {
    const isSelected = selected.find((el) => el.guid === playlist.guid);
    if (isSelected) {
      setSelected(selected.filter((el) => el.guid !== isSelected.guid));
    } else {
      setSelected([...selected, playlist]);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { failedCount, addedPlaylists } = await addAudioToPlaylists(
        selected
      );
      if (failedCount) {
        setSelected(selected.filter((el) => !addedPlaylists.includes(el.guid)));
      } else {
        onClose();
      }
    } catch (e) {
      // proceed
    } finally {
      if (isMounted()) {
        setSubmitting(false);
      }
    }
  };

  const handleCreatePlaylist = () => {
    if (limitReached) {
      showLimitReachedPopup();
    } else {
      setOpen(false);
      createPlaylist();
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          data-testid="dialog-title"
          display="flex"
          justifyContent="space-between"
          pr={0}
        >
          Add To Playlist
          <Button
            variant="text"
            size="sm"
            color="B.0"
            onClick={handleCreatePlaylist}
            isDisabled={submitting}
            data-testid="create-playlist"
          >
            Create Playlist
          </Button>
        </ModalHeader>
        <ModalBody
          id={PLAYLISTS_SELECT_SCROLL_ID}
          data-testid="dialog-content"
          maxHeight="400px"
          overflow="hidden"
          overflowY="auto"
        >
          <PlaylistsSelectList
            playlists={playlists}
            loading={playlistsLoading}
            selectedPlaylists={selected}
            maxPlaylistAudios={maxPlaylistAudiosCount}
            onPlaylistSelect={handleSelectPlaylist}
          />
        </ModalBody>
        <ModalFooter data-testid="dialog-actions">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            isDisabled={!selected.length || submitting}
            onClick={handleSubmit}
            data-testid="add-audio"
            ml={2}
          >
            Add Audio
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
